import React, { FC, useContext, useRef, useMemo, useState } from 'react';
import { Box, Button, Typography, Radio, TextField } from '@mui/material';

import TableActionButtons from './TableActionButtons';
import { Client } from '../../../api/clients/types';
import { Context as ClientsContext } from '../../../api/clients/context';
import { Context as CurrenciesContext } from '../../../api/currencies/context';
import { Context as ExchangeRatesContext } from '../../../api/exchangeRates/context';
import { Currency } from '../../../api/currencies/types';
import { recomputeRateRows, RateRow } from '../../../utils/currencyMath';
import { taytanFormatNumberToString } from '../../../utils/mathUtils';

export interface Props {
    currenciesArray?: any[];
    setChangeRate?: Function;
    setRate: Function;
    setAmount?: (amount: number) => void;
    amount: number;
    documentCurrency: Currency;
    savedRate?: number;
    /**
     * Inherited source-document rate (document currency → local). When provided
     * (> 0), the grid defaults to this rate instead of the live market rate, and
     * two buttons appear: "Latest Rate" (market) and "Document Rate" (restore
     * this inherited rate).
     */
    documentRate?: number;
}

interface RateCurrency {
    exchangeRateBuy: number;
    exchangeRateSell: number;
}

interface ChangeRateCurrency {
    currency: string;
    rate: RateCurrency | number;
    amount?: number | string;
    edit?: boolean;
    isPrimaryCurrency?: boolean;
    isLocalCurrency?: boolean;
    isDocumentCurrency?: boolean;
}

const ChangeRate: FC<Props> = (props: Props) => {
    const { setChangeRate, documentCurrency, setRate, setAmount, amount, documentRate } = props;
    const hasDocumentRate = !!documentRate && documentRate > 0;

    const [selectedCurrency, setSelectedCurrency] = useState<string | null>(documentCurrency?.code);
    const selectedCurrencyRef = useRef<string | null>(selectedCurrency);

    const clientsContext = useContext(ClientsContext);
    const currenciesContext = useContext(CurrenciesContext);
    const { getConvertRate } = useContext(ExchangeRatesContext);

    const currencySettings = useMemo(() => {
        return (clientsContext.selected as Client)?.currencySettings;
    }, [clientsContext.selected]);

    const currencyOptions = useMemo(() => currenciesContext.dataAsArray || [], [currenciesContext.dataAsArray]);

    const primaryCurrency: Currency | undefined = useMemo(() => {
        return currencyOptions.find((c: Currency) => c._id === currencySettings?.primaryCurrency);
    }, [currencyOptions, currencySettings?.primaryCurrency]);

    const localCurrency: Currency | undefined = useMemo(() => {
        return currencyOptions.find((c: Currency) => c._id === currencySettings?.localCurrency);
    }, [currencyOptions, currencySettings?.localCurrency]);

    const getExchangeRateSell = (rate: RateCurrency | number) =>
        typeof rate === 'object' ? rate.exchangeRateSell : rate;

    // docToLocalOverride: when provided (> 0), use it as the document→local rate
    // instead of the live market rate (e.g. the inherited document rate).
    const buildRowData = (docToLocalOverride?: number): ChangeRateCurrency[] => {
        if (!localCurrency || !primaryCurrency || !documentCurrency?.code) return [];
        const data: ChangeRateCurrency[] = [];

        const docToLocalMarketRate = getExchangeRateSell(getConvertRate(documentCurrency._id, localCurrency._id) || 0);
        const docToLocalRate: number = documentCurrency.code === localCurrency.code
            ? 1
            : (docToLocalOverride && docToLocalOverride > 0 ? docToLocalOverride : (docToLocalMarketRate || 1));

        const ilsAmount = amount * docToLocalRate;

        const primaryToLocalRate: number = primaryCurrency.code === localCurrency.code
            ? 1
            : getExchangeRateSell(getConvertRate(primaryCurrency._id, localCurrency._id) || 0);

        if (documentCurrency.code !== primaryCurrency.code && documentCurrency.code !== localCurrency.code) {
            data.push({
                currency: documentCurrency.code,
                rate: docToLocalRate,
                amount,
                edit: true,
                isDocumentCurrency: true
            });
        }

        let primaryAmount: number;
        let primaryRate: number;
        if (primaryCurrency.code === documentCurrency.code) {
            primaryAmount = amount;
            primaryRate = docToLocalRate;
        } else {
            primaryRate = primaryToLocalRate;
            primaryAmount = primaryRate > 0 ? ilsAmount / primaryRate : amount;
        }
        data.push({
            currency: primaryCurrency.code,
            rate: primaryRate,
            amount: primaryAmount,
            edit: true,
            isPrimaryCurrency: true,
            isDocumentCurrency: documentCurrency.code === primaryCurrency.code
        });

        if (documentCurrency.code !== localCurrency.code) {
            data.push({ currency: localCurrency.code, rate: 1, amount: ilsAmount, edit: false, isLocalCurrency: true });
        } else if (data.length < 2) {
            data.push({
                currency: localCurrency.code,
                rate: 1,
                amount: ilsAmount,
                edit: false,
                isLocalCurrency: true,
                isDocumentCurrency: true
            });
        }
        return data;
    };

    const [rowData, setRowData] = useState<ChangeRateCurrency[]>(() =>
        buildRowData(hasDocumentRate ? documentRate : undefined)
    );

    // Amounts show thousands separators when NOT being edited; while the user is
    // typing in a field we show the raw text so commas/decimals stay typeable
    // (a formatted controlled value would fight the cursor and drop a trailing ".").
    const [amountEdit, setAmountEdit] = useState<{ currency: string; text: string } | null>(null);

    const handleRadioChange = (currency: string) => {
        setSelectedCurrency(currency);
        selectedCurrencyRef.current = currency;
    };

    // Apply an amount/rate edit and re-derive the other rows. The RADIO-selected
    // row is the ANCHOR whose AMOUNT stays fixed on a rate change (TB behavior).
    const applyChange = (editedCurrency: string, field: 'amount' | 'rate', rawValue: number) => {
        // Delegate the anchor-aware re-derivation to the shared, unit-tested
        // helper. For a RATE edit the radio-selected row is the anchor. For an
        // AMOUNT edit the value the user typed IS the truth to keep, so the edited
        // row becomes the anchor — its amount stays and every OTHER row's rate
        // (including the DOCUMENT row that OK persists) re-derives. Without this,
        // editing the USD amount only changed the USD→ILS rate, which the OK
        // handler discards (it pushes back the document row), so the edit was lost.
        const anchor = field === 'amount' ? editedCurrency : selectedCurrencyRef.current;
        setRowData(
            prevRows =>
                recomputeRateRows(
                    prevRows as unknown as RateRow[],
                    editedCurrency,
                    field,
                    rawValue,
                    anchor
                ) as unknown as typeof prevRows
        );
    };

    const num = (n: any) => (Number(n) || 0);
    const cell = { display: 'flex', alignItems: 'center' } as const;
    const GRID = '24px 46px 130px 92px';

    return (
        <Box className="change-rate-dialog" sx={{ width: '100%' }}>
            {/* Header */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: GRID,
                    gap: 2,
                    mb: 0.5,
                    color: 'text.secondary',
                    fontSize: 12,
                    fontWeight: 600,
                    '& > *': { textAlign: 'left' }
                }}
            >
                <Box sx={{ gridColumn: '1 / 3' }}>Currency</Box>
                <Box>Amount</Box>
                <Box>Rate</Box>
            </Box>

            {rowData.map((row) => (
                <Box
                    key={row.currency + (row.isPrimaryCurrency ? '-p' : row.isLocalCurrency ? '-l' : '-d')}
                    sx={{ display: 'grid', gridTemplateColumns: GRID, gap: 2, alignItems: 'center', mb: 0.75 }}
                >
                    <Box sx={cell}>
                        <Radio
                            checked={selectedCurrency === row.currency}
                            onChange={() => handleRadioChange(row.currency)}
                            size="small"
                            sx={{ p: 0.25 }}
                        />
                    </Box>
                    <Box sx={{ ...cell, fontWeight: 500 }}>{row.currency}</Box>
                    <TextField
                        size="small"
                        fullWidth
                        value={
                            amountEdit?.currency === row.currency
                                ? amountEdit.text
                                : taytanFormatNumberToString(Math.round(num(row.amount) * 100) / 100)
                        }
                        onFocus={() =>
                            setAmountEdit({
                                currency: row.currency,
                                text: String(Math.round(num(row.amount) * 100) / 100)
                            })
                        }
                        onChange={(e) => {
                            const text = e.target.value;
                            setAmountEdit({ currency: row.currency, text });
                            const parsed = parseFloat(text.replace(/,/g, ''));
                            if (!Number.isNaN(parsed)) applyChange(row.currency, 'amount', parsed);
                        }}
                        onBlur={() => setAmountEdit(null)}
                        onKeyDown={(e) => {
                            // Enter just blurs the field (commits the value and
                            // re-applies comma formatting via onBlur) — no submit.
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                (e.target as HTMLInputElement).blur();
                            }
                        }}
                        sx={{ minWidth: 0 }}
                        inputProps={{ inputMode: 'decimal', style: { textAlign: 'right', padding: '6px 8px' } }}
                    />
                    <TextField
                        type="number"
                        size="small"
                        fullWidth
                        value={row.isLocalCurrency ? 1 : Math.round(num(row.rate) * 1e4) / 1e4}
                        disabled={!row.edit || !!row.isLocalCurrency}
                        onChange={(e) => applyChange(row.currency, 'rate', parseFloat(e.target.value))}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                (e.target as HTMLInputElement).blur();
                            }
                        }}
                        sx={{ minWidth: 0 }}
                        inputProps={{ step: 'any', style: { textAlign: 'right', padding: '6px 8px' } }}
                    />
                </Box>
            ))}

            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5, ml: 0.5 }}>
                Select the row to keep fixed, then edit its rate.
            </Typography>

            {hasDocumentRate && (
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Button size="small" variant="outlined" onClick={() => setRowData(buildRowData())}>
                        Latest Rate
                    </Button>
                    <Button size="small" variant="outlined" onClick={() => setRowData(buildRowData(documentRate))}>
                        Document Rate
                    </Button>
                </Box>
            )}

            <TableActionButtons
                setDefaultValues={null}
                setChangeRate={setChangeRate}
                rowData={rowData}
                setRate={setRate}
                setAmount={setAmount}
            />
        </Box>
    );
};

export default ChangeRate;
